import React, { useEffect, useMemo, useState } from "react";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

function onlyNumberOrEmpty(v) {
  const s = (v ?? "").toString().trim();
  if (!s) return "";
  const norm = s.replace(",", ".");
  if (!/^\d+(\.\d+)?$/.test(norm)) return s;
  return norm;
}

export default function CriarObjetivosDialogIndividuais({
  open,
  onClose,
  onApply,

  // array atual [{ refvendedor, qtd, euros, litros, clientes }]
  value = [],

  // opções [{value,label}]
  vendedoresOptions = [],

  loading = false,

  usaQtd = true,
  usaEuros = true,
  usaLitros = true,
  usaClientes = true,
}) {
  const [vendedoresSel, setVendedoresSel] = useState([]); // array {value,label}
  const [linhas, setLinhas] = useState({}); // por refvendedor: {qtd, euros, litros, clientes}

  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (!open) return;

    setHydrated(false);

    const refs = (value || []).map((x) => String(x.refvendedor));
    const sel = (vendedoresOptions || []).filter((o) => refs.includes(String(o.value)));
    setVendedoresSel(sel);

    const obj = {};
    for (const it of value || []) {
      const r = String(it.refvendedor);
      obj[r] = {
        qtd: it.qtd != null ? String(it.qtd) : "",
        euros: it.euros != null ? String(it.euros) : "",
        litros: it.litros != null ? String(it.litros) : "",
        clientes: it.clientes != null ? String(it.clientes) : "",
      };
    }
    setLinhas(obj);

    // ✅ garantir 1 frame com hydrated=false (spinner visível)
    requestAnimationFrame(() => setHydrated(true));
  }, [open, value, vendedoresOptions]);

  const refsSelecionados = useMemo(
    () => (vendedoresSel || []).map((x) => String(x.value)).filter(Boolean),
    [vendedoresSel]
  );

  // garantir que existem linhas para refs selecionadas
  useEffect(() => {
    if (!open) return;

    setLinhas((prev) => {
      const novo = { ...(prev || {}) };
      for (const r of refsSelecionados) {
        if (!novo[r]) {
          novo[r] = { qtd: "", euros: "", litros: "", clientes: "" };
        }
      }
      // remover linhas de vendedores deselecionados
      Object.keys(novo).forEach((k) => {
        if (!refsSelecionados.includes(k)) delete novo[k];
      });
      return novo;
    });
  }, [refsSelecionados, open]);

  const setCampo = (ref, campo, valor) => {
    setLinhas((prev) => ({
      ...(prev || {}),
      [ref]: { ...(prev?.[ref] || {}), [campo]: valor },
    }));
  };

  const handleApply = () => {
    // construir payload
    const out = refsSelecionados.map((r) => {
      const l = linhas?.[r] || {};
      return {
        refvendedor: r,
        qtd: usaQtd && (l.qtd ?? "").toString().trim() !== "" ? Number(l.qtd) : null,
        euros: usaEuros && (l.euros ?? "").toString().trim() !== "" ? Number(l.euros) : null,
        litros: usaLitros && (l.litros ?? "").toString().trim() !== "" ? Number(l.litros) : null,
        clientes:
          usaClientes && (l.clientes ?? "").toString().trim() !== "" ? Number(l.clientes) : null,
      };
    });

    onApply(out);
  };

  const podeAplicar = refsSelecionados.length > 0;

  const handleEnterFixar = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    e.stopPropagation();

    // força commit do valor (como clicar fora)
    e.currentTarget.blur();
  };

  const totais = useMemo(() => {
    const acc = { qtd: 0, euros: 0, litros: 0, clientes: 0 };

    const toNum = (v) => {
      const s = (v ?? "").toString().trim();
      if (!s) return 0;
      const n = Number(s.replace(",", "."));
      return Number.isFinite(n) ? n : 0;
    };

    for (const r of refsSelecionados) {
      const l = linhas?.[r] || {};
      if (usaQtd) acc.qtd += toNum(l.qtd);
      if (usaEuros) acc.euros += toNum(l.euros);
      if (usaLitros) acc.litros += toNum(l.litros);
      if (usaClientes) acc.clientes += toNum(l.clientes);
    }

    return acc;
  }, [refsSelecionados, linhas, usaQtd, usaEuros, usaLitros, usaClientes]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Objetivos Individuais</DialogTitle>

      <MDBox
        px={3}
        py={2}
        sx={{
          borderBottom: "1px solid rgba(0,0,0,0.12)",
          backgroundColor: "rgba(0,0,0,0.02)",
        }}
      >
        <MDBox display="flex" flexWrap="wrap" gap={2} alignItems="center">
          {usaQtd && (
            <MDBox sx={{ minWidth: 90 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Quantidade
              </MDTypography>
              <TextField
                value={totais.qtd ? String(totais.qtd) : ""}
                InputProps={{ readOnly: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, width: 90 } }}
              />
            </MDBox>
          )}

          {usaEuros && (
            <MDBox sx={{ minWidth: 90 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Euros
              </MDTypography>
              <TextField
                value={totais.euros ? String(totais.euros) : ""}
                InputProps={{ readOnly: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, width: 90 } }}
              />
            </MDBox>
          )}

          {usaLitros && (
            <MDBox sx={{ minWidth: 90 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Litros
              </MDTypography>
              <TextField
                value={totais.litros ? String(totais.litros) : ""}
                InputProps={{ readOnly: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, width: 90 } }}
              />
            </MDBox>
          )}

          {usaClientes && (
            <MDBox sx={{ minWidth: 90 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Clientes
              </MDTypography>
              <TextField
                value={totais.clientes ? String(totais.clientes) : ""}
                InputProps={{ readOnly: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, width: 90 } }}
              />
            </MDBox>
          )}
        </MDBox>
      </MDBox>

      <DialogContent dividers>
        {loading || !hydrated || (vendedoresOptions?.length || 0) === 0 ? (
          <MDBox py={4} display="flex" alignItems="center" justifyContent="center" gap={1}>
            <CircularProgress size={22} />
            <MDTypography variant="button" sx={{ fontSize: 12, opacity: 0.8 }}>
              A carregar objetivos individuais...
            </MDTypography>
          </MDBox>
        ) : (
          <>
            <MDBox mb={2}>
              <MDTypography variant="caption" color="text">
                Seleciona um ou mais vendedores e define os mínimos individuais.
              </MDTypography>
            </MDBox>

            <MDBox>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5, display: "block" }}>
                Vendedores
              </MDTypography>

              <Autocomplete
                multiple
                options={vendedoresOptions || []}
                value={vendedoresSel}
                onChange={(_e, v) => {
                  // ✅ garante que qualquer TextField em edição "fixa" o valor
                  if (document.activeElement && typeof document.activeElement.blur === "function") {
                    document.activeElement.blur();
                  }

                  const next = v || [];

                  setVendedoresSel((prev) => {
                    const prevArr = prev || [];

                    // ids (value) atuais
                    const prevIds = prevArr.map((x) => String(x?.value));
                    const nextIds = next.map((x) => String(x?.value));

                    // removidos (mantém a ordem anterior)
                    const kept = prevArr.filter((x) => nextIds.includes(String(x?.value)));

                    // adicionados (meter no topo)
                    const added = next.filter((x) => !prevIds.includes(String(x?.value)));

                    // added no topo, kept a seguir (ordem antiga), e garantir únicos
                    const out = [...added, ...kept];

                    const seen = new Set();
                    return out.filter((x) => {
                      const id = String(x?.value);
                      if (seen.has(id)) return false;
                      seen.add(id);
                      return true;
                    });
                  });
                }}
                getOptionLabel={(o) => o?.label ?? ""}
                isOptionEqualToValue={(a, b) => String(a?.value) === String(b?.value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Seleciona um ou mais vendedores..."
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
                  />
                )}
              />
            </MDBox>

            {refsSelecionados.length > 0 && (
              <MDBox mt={2}>
                <MDTypography variant="button" sx={{ fontSize: 12, mb: 1, display: "block" }}>
                  Metas por vendedor
                </MDTypography>

                {refsSelecionados.map((r) => {
                  const vendedorLabel =
                    (vendedoresOptions || []).find((x) => String(x.value) === String(r))?.label ||
                    r;

                  const l = linhas?.[r] || {};

                  return (
                    <MDBox
                      key={r}
                      mb={2}
                      p={2}
                      sx={{
                        borderRadius: 2,
                        border: "1px solid rgba(0,0,0,0.08)",
                        backgroundColor: "rgba(255,255,255,0.4)",
                      }}
                    >
                      <MDTypography variant="button" sx={{ fontSize: 12, mb: 1, display: "block" }}>
                        {vendedorLabel}
                      </MDTypography>

                      <MDBox display="flex" flexWrap="wrap" gap={2}>
                        {usaQtd && (
                          <MDBox sx={{ minWidth: 90 }}>
                            <MDTypography
                              variant="button"
                              sx={{ fontSize: 12, mb: 0.5, display: "block" }}
                            >
                              Quantidade
                            </MDTypography>
                            <TextField
                              value={l.qtd ?? ""}
                              onChange={(e) =>
                                setCampo(r, "qtd", onlyNumberOrEmpty(e.target.value))
                              }
                              onKeyDown={handleEnterFixar}
                              inputProps={{ step: "0.01", min: "0" }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                  fontSize: 13,
                                  width: 90,
                                },
                              }}
                            />
                          </MDBox>
                        )}

                        {usaEuros && (
                          <MDBox sx={{ minWidth: 90 }}>
                            <MDTypography
                              variant="button"
                              sx={{ fontSize: 12, mb: 0.5, display: "block" }}
                            >
                              Euros
                            </MDTypography>
                            <TextField
                              value={l.euros ?? ""}
                              onChange={(e) =>
                                setCampo(r, "euros", onlyNumberOrEmpty(e.target.value))
                              }
                              onKeyDown={handleEnterFixar}
                              inputProps={{ step: "0.01", min: "0" }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                  fontSize: 13,
                                  width: 90,
                                },
                              }}
                            />
                          </MDBox>
                        )}

                        {usaLitros && (
                          <MDBox sx={{ minWidth: 90 }}>
                            <MDTypography
                              variant="button"
                              sx={{ fontSize: 12, mb: 0.5, display: "block" }}
                            >
                              Litros
                            </MDTypography>
                            <TextField
                              value={l.litros ?? ""}
                              onChange={(e) =>
                                setCampo(r, "litros", onlyNumberOrEmpty(e.target.value))
                              }
                              onKeyDown={handleEnterFixar}
                              inputProps={{ step: "0.01", min: "0" }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                  fontSize: 13,
                                  width: 90,
                                },
                              }}
                            />
                          </MDBox>
                        )}

                        {usaClientes && (
                          <MDBox sx={{ minWidth: 90 }}>
                            <MDTypography
                              variant="button"
                              sx={{ fontSize: 12, mb: 0.5, display: "block" }}
                            >
                              Clientes
                            </MDTypography>
                            <TextField
                              value={l.clientes ?? ""}
                              onChange={(e) =>
                                setCampo(r, "clientes", onlyNumberOrEmpty(e.target.value))
                              }
                              onKeyDown={handleEnterFixar}
                              inputProps={{ step: "0.01", min: "0" }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                  fontSize: 13,
                                  width: 90,
                                },
                              }}
                            />
                          </MDBox>
                        )}
                      </MDBox>
                    </MDBox>
                  );
                })}
              </MDBox>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <MDButton color="info" onClick={onClose}>
          Fechar
        </MDButton>
        <MDButton color="success" onClick={handleApply} disabled={loading || !podeAplicar}>
          Aplicar
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
